/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { pageStateSelectors } from '../../../app/page/page-state/page-state.selectors';
import { useSelectWithParams } from '../../../utils/selector.utils';
import { useApiRequestState } from '../../../utils/api.utils';
import { PageLayout } from '../common/PageLayout';
import { WidgetWrapper } from '../common/widgets/WidgetWrapper';
import { initialKpiPage } from '../../../app/page/kpi/kpi.actions';
import { updateScoresBulk } from '../../../app/page/page-storage/kpi/kpi.actions';
import { MeetingDashboardNavbar } from '../common/navigation-bars/MeetigDashboardNavbar';
import { WidgetKpiSettings } from '../common/widgets/widgets-kpi/WidgetKpiSettings';
import { ScoreFormTrigger } from '../../dialogs/ScoreFormDialog/ScoreFormTrigger';

import ExportIcon from '../../resources/images/new-icons/export-icon.svg';
import SearchIcon from '../../resources/images/search-icon.svg';
import { UserFilter } from './UserFilter';
import { MemberSelect } from './MemberSelect';
import { IMeetingSimple } from '../../../app/resource/meeting-simple/meeting-simple.types';
import { meetingSimpleSelectors } from '../../../app/resource/meeting-simple/meeting-simple.selectors';
import { updateMeetingSimple } from '../../../app/page/page-storage/meetings-simple/meetings-simple.actions';

export const KpiPage = () => {
  const dispatch = useDispatch();
  const showLoader = useSelectWithParams(pageStateSelectors.selectPageInProgress, 'kpi');
  const { meetingId } = useParams<{ meetingId: IMeetingSimple['id'] }>();
  const [filterId, setFilterId] = useState('');
  const [selectedScoreIds, setSelectedScoreIds] = useState<string[]>([]);
  const [isUpdating] = useApiRequestState('updateScoresBulkApi');
  const [kpisGroupedByUser, setKpisGroupedByUser] = useState(false);

  const handleFilterId = useCallback((filterId) => {
    setFilterId(filterId);
  }, []);

  const handleMemberSelect = useCallback(
    (userId) => {
      if (selectedScoreIds.length > 0) {
        dispatch(updateScoresBulk(selectedScoreIds, userId));
      }

      setSelectedScoreIds([]);
    },
    [dispatch, selectedScoreIds],
  );

  const handleScoreSelection = useCallback((scoreId: string, isSelected: boolean) => {
    if (isSelected) {
      setSelectedScoreIds((prev) => {
        const newIds = [...prev, scoreId];
        return newIds;
      });
    } else {
      setSelectedScoreIds((prev) => {
        const newIds = prev.filter((id) => id !== scoreId);
        return newIds;
      });
    }
  }, []);

  useEffect(() => {
    dispatch(initialKpiPage(meetingId));
  }, [dispatch, meetingId]);

  const meeting = useSelectWithParams(meetingSimpleSelectors.selectResourceById, meetingId);
  const userIds = useMemo(() => (meeting ? [...meeting.shareWithIds, ...meeting.administratorIds] : []), [meeting]);

  const uniqueUserIds = Array.from(new Set(userIds));

  // Initialize kpisGroupedByUser from meeting data
  useEffect(() => {
    if (meeting?.kpisGroupedByUser !== undefined) {
      setKpisGroupedByUser(meeting.kpisGroupedByUser);
    }
  }, [meeting]);

  const handleKpisGroupedByUserChange = useCallback(
    (newValue: boolean) => {
      setKpisGroupedByUser(newValue);
      if (meetingId && meeting) {
        // Use the existing updateMeetingSimple action with minimal required fields
        // The API accepts partial updates, so we only need to send kpisGroupedByUser
        dispatch(
          updateMeetingSimple(meetingId, {
            kpisGroupedByUser: newValue,
          }),
        );
      }
    },
    [dispatch, meetingId, meeting],
  );
  return (
    <PageLayout
      showLoader={showLoader}
      smallSidebar
      externalLogicNavbar={<MeetingDashboardNavbar showLoader={showLoader} meetingId={meetingId} />}
    >
      {!showLoader ? (
        <>
          <WidgetWrapper
            name="Scorecard Settings"
            actionElement={
              <div className="ml-auto flex aic jcr">
                {(selectedScoreIds.length > 0 || isUpdating) && (
                  <MemberSelect userIds={uniqueUserIds} onMemberSelect={handleMemberSelect} />
                )}
                <div className="flex aic mr-2">
                  <p className="switch-inline-text mr-1">Group by user</p>
                  <label className="switch big-switch mr-1 mb-0" htmlFor="groupByUser">
                    <input
                      type="checkbox"
                      className="switch-input change-theme"
                      id="groupByUser"
                      name="groupByUser"
                      onChange={() => handleKpisGroupedByUserChange(!kpisGroupedByUser)}
                      checked={kpisGroupedByUser}
                    />
                    <span className="switch-label"></span>
                    <span className="switch-handle"></span>
                  </label>
                </div>
                <UserFilter userIds={uniqueUserIds} setFilterId={handleFilterId} />
                <a className="btn btn-md btn-border white-bg px-1">
                  <img src={ExportIcon} alt="export" className="img-fluid mr-1" style={{ width: 8 }} />
                  Export
                </a>
                <a className="btn btn-md btn-border white-bg px-1">
                  <img src={SearchIcon} alt="search" className="img-fluid" />
                </a>
                <ScoreFormTrigger meetingId={meetingId} />
              </div>
            }
          >
            <WidgetKpiSettings
              filterId={filterId}
              onScoreSelection={handleScoreSelection}
              selectedScoreIds={selectedScoreIds}
              kpisGroupedByUser={kpisGroupedByUser}
              userIds={uniqueUserIds}
            />
          </WidgetWrapper>
        </>
      ) : (
        <></>
      )}
    </PageLayout>
  );
};
